/**
 * Clipboard payloads for an assistant answer.
 *
 * The copy button puts two flavors on the clipboard at once: `text/html`
 * for Word, Outlook, Teams and Confluence (headings, lists and tables keep
 * their formatting) and `text/plain` carrying the Markdown source for
 * terminals, IDEs and note apps. Citation markers stay readable as `[N]`
 * and a "Sources" block lists the cited documents with their pages, so a
 * pasted answer remains verifiable. A plain-text variant (Markdown syntax
 * removed) serves ticket comments and chat fields.
 */

import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import type { ChatSource } from '../../api/chatTypes';
import { KEINE_QUELLEN_RE } from './MarkdownMessage';

export interface CopyTexts {
  sourcesHeading: string;
  pageAbbrev: string;
}

export interface CopyPayload {
  /** Markdown source with `[N]` markers and a sources block. */
  markdown: string;
  /** Inline-styled HTML for rich-text targets. */
  html: string;
  /** Markdown syntax removed, markers and sources kept. */
  text: string;
}

const MARKER_RE = /\[\[(\d+)(?:\.\d+)?\]\]/g;

/** `[[1]]` -> `[1]`; a run `[1][1]` collapses. */
export function readableMarkers(content: string): string {
  return content
    .replace(KEINE_QUELLEN_RE, '')
    .replace(MARKER_RE, '[$1]')
    .replace(/(\[\d+\])(?:\1)+/g, '$1')
    .trim();
}

function pagesOf(source: ChatSource): number[] {
  const pages = new Set<number>();
  const locators = source.locators && source.locators.length > 0 ? source.locators : source.locator ? [source.locator] : [];
  for (const loc of locators) {
    if (loc && (loc.loc_kind === 'page' || loc.loc_kind === 'slide' || loc.loc_kind === 'region')) {
      for (const p of loc.positions ?? []) {
        pages.add(p);
      }
    }
  }
  return [...pages].sort((a, b) => a - b);
}

/** Cited documents in citation order, deduplicated per document number. */
export function sourceLines(sources: ChatSource[], texts: CopyTexts): string[] {
  const byNumber = new Map<string, string>();
  for (const source of sources) {
    if (source.citation_id === null || source.citation_id === undefined) {
      continue;
    }
    const number = String(source.citation_id).split('.')[0];
    if (byNumber.has(number)) {
      continue;
    }
    const pages = pagesOf(source);
    const pageText = pages.length > 0 ? `, ${texts.pageAbbrev} ${pages.join(', ')}` : '';
    byNumber.set(number, `[${number}] ${source.title}${pageText}`);
  }
  return [...byNumber.entries()]
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, line]) => line);
}

/** Simple Markdown-to-plain-text: headings, emphasis, links, tables, fences. */
export function markdownToPlain(markdown: string): string {
  const lines = markdown.split('\n');
  const out: string[] = [];
  let inFence = false;
  for (const raw of lines) {
    if (/^\s*(```|~~~)/.test(raw)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) {
      out.push(raw);
      continue;
    }
    if (/^\s*\|?\s*:?-{2,}/.test(raw) && raw.includes('-') && /^[\s|:-]+$/.test(raw)) {
      continue; // table separator row
    }
    let line = raw
      .replace(/^\s{0,3}#{1,6}\s+/, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      .replace(/(?<![A-Za-z0-9])[*_](.+?)[*_](?![A-Za-z0-9])/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
      .replace(/^\s*>\s?/, '');
    if (line.trim().startsWith('|')) {
      line = line
        .trim()
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((c) => c.trim())
        .join('\t');
    }
    out.push(line);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

const HTML_STYLES: Array<[RegExp, string]> = [
  [/<h1>/g, '<h1 style="font-size:1.4em;margin:0.6em 0 0.3em">'],
  [/<h2>/g, '<h2 style="font-size:1.25em;margin:0.6em 0 0.3em">'],
  [/<h3>/g, '<h3 style="font-size:1.1em;margin:0.5em 0 0.25em">'],
  [/<p>/g, '<p style="margin:0 0 0.6em">'],
  [/<table>/g, '<table style="border-collapse:collapse;margin:0.4em 0">'],
  [/<th>/g, '<th style="border:1px solid #999;padding:4px 8px;text-align:left;background:#f2f2f2">'],
  [/<td>/g, '<td style="border:1px solid #999;padding:4px 8px;vertical-align:top">'],
  [/<pre>/g, '<pre style="white-space:pre;font-family:Consolas,Menlo,monospace;font-size:0.9em;background:#f5f5f5;padding:8px;border:1px solid #ddd">'],
  [/<code>/g, '<code style="font-family:Consolas,Menlo,monospace;font-size:0.95em">'],
  [/<blockquote>/g, '<blockquote style="margin:0.4em 0;padding-left:0.8em;border-left:3px solid #ccc;color:#555">'],
];

/** Render Markdown to inline-styled HTML; `[N]` markers become superscripts. */
export function markdownToHtml(markdown: string): string {
  let html = renderToStaticMarkup(React.createElement(ReactMarkdown, null, markdown));
  for (const [re, replacement] of HTML_STYLES) {
    html = html.replace(re, replacement);
  }
  html = html.replace(/\[(\d+)\]/g, '<sup>[$1]</sup>');
  return `<div style="font-family:Segoe UI,Arial,sans-serif;font-size:11pt;line-height:1.4">${html}</div>`;
}

/** Build all clipboard flavors for one answer. */
export function buildCopyPayload(content: string, sources: ChatSource[], texts: CopyTexts): CopyPayload {
  const body = readableMarkers(content);
  const lines = sourceLines(sources, texts);
  const sourcesMd = lines.length > 0 ? `\n\n${texts.sourcesHeading}:\n${lines.map((l) => `- ${l}`).join('\n')}` : '';
  const markdown = `${body}${sourcesMd}`;
  const sourcesText = lines.length > 0 ? `\n\n${texts.sourcesHeading}:\n${lines.join('\n')}` : '';
  const text = `${markdownToPlain(body)}${sourcesText}`;
  const sourcesHtml =
    lines.length > 0
      ? `<p style="margin:0.8em 0 0.2em"><strong>${escapeHtml(texts.sourcesHeading)}:</strong></p><ol style="margin:0;padding-left:1.4em">${lines
          .map((l) => `<li>${escapeHtml(l.replace(/^\[\d+\]\s*/, ''))}</li>`)
          .join('')}</ol>`
      : '';
  const html = markdownToHtml(body).replace(/<\/div>$/, `${sourcesHtml}</div>`);
  return { markdown, html, text };
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Write to the clipboard: both flavors where `ClipboardItem` exists (secure
 * context, modern browsers), the Markdown text otherwise. Must be called
 * synchronously from the click handler (Safari requires the user gesture).
 */
export async function writeClipboard(payload: { html?: string; text: string }): Promise<void> {
  if (!('clipboard' in navigator)) {
    throw new Error('clipboard unavailable');
  }
  const clipboard = navigator.clipboard;
  if (payload.html && typeof ClipboardItem !== 'undefined' && typeof clipboard.write === 'function') {
    const item = new ClipboardItem({
      'text/html': new Blob([payload.html], { type: 'text/html' }),
      'text/plain': new Blob([payload.text], { type: 'text/plain' }),
    });
    await clipboard.write([item]);
    return;
  }
  await clipboard.writeText(payload.text);
}
